import React from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { AsyncJobDTO } from '@recurrency/core-api-schema/dist/asyncJobs/common';
import { colors } from 'theme/colors';

import { Button } from 'components/Button';
import { Card, CardHeader, CardSection } from 'components/Card';
import { FlexSpace } from 'components/FlexSpace';
import { CenteredLoader } from 'components/Loaders';
import { NoDataMessage } from 'components/NoDataMessage';
import { Typography } from 'components/Typography';

import { useCoreApi } from 'hooks/useApi';

import { formatDateWithTimezone, formatDuration } from 'utils/formatting';

import { JobStatusBadge } from '../JobStatusBadge';
import { MinMaxChangesTable } from './MinMaxChangesTable';
import { countItemsFromSteps, getMinMaxChanges, sortByLargestChange } from './utils';

const TOP_CHANGES_SHOWN = 5;

function Stat({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div>
      <div
        className={css`
          font-size: 20px;
          font-weight: 600;
          color: ${color ?? colors.neutral[900]};
        `}
      >
        {value}
      </div>
      <div
        className={css`
          font-size: 12px;
          color: ${colors.neutral[500]};
        `}
      >
        {label}
      </div>
    </div>
  );
}

/**
 * Summary of the most recent automated run plus its largest few changes.
 *
 * Deliberately not the full change list: a run can cover up to the nightly cap, so the whole list
 * lives in the modal and this card links to it.
 */
export function LastRunCard({ lastRun, onViewAll }: { lastRun?: AsyncJobDTO; onViewAll: (jobId: string) => void }) {
  const { data: asyncJob, isLoading } = useCoreApi(
    schemas.asyncJobs.getAsyncJob,
    lastRun ? { pathParams: { jobId: lastRun.id } } : null,
  );

  const changes = React.useMemo(() => sortByLargestChange(getMinMaxChanges(asyncJob?.steps ?? [])), [asyncJob]);
  // the card already has this run's steps, so its counts come straight from the payloads
  const itemCounts = React.useMemo(() => countItemsFromSteps(asyncJob?.steps ?? []), [asyncJob]);

  return (
    <Card
      className={css`
        height: 100%;
      `}
    >
      <CardHeader
        title="Last run"
        description={lastRun?.createdAt ? formatDateWithTimezone(lastRun.createdAt) : 'Not run yet'}
        headerActions={lastRun ? <JobStatusBadge status={lastRun.status} /> : undefined}
      />
      {!lastRun ? (
        <CardSection>
          <NoDataMessage />
        </CardSection>
      ) : (
        <>
          <CardSection>
            <FlexSpace direction="row" gap={32}>
              <Stat
                label="Exported"
                value={itemCounts.exported}
                color={itemCounts.exported > 0 ? colors.success[600] : undefined}
              />
              <Stat
                label="Failed"
                value={itemCounts.failed}
                color={itemCounts.failed > 0 ? colors.danger[600] : undefined}
              />
              <Stat label="Duration" value={formatDuration(lastRun.durationMs ?? 0)} />
            </FlexSpace>
          </CardSection>
          <CardSection>
            <Typography style={{ fontWeight: 600, marginBottom: 8 }}>Largest changes</Typography>
            {isLoading ? (
              <CenteredLoader />
            ) : changes.length === 0 ? (
              <NoDataMessage />
            ) : (
              <>
                <MinMaxChangesTable changes={changes.slice(0, TOP_CHANGES_SHOWN)} pageSize={TOP_CHANGES_SHOWN} />
                {changes.length > TOP_CHANGES_SHOWN && (
                  <Button type="link" onClick={() => onViewAll(lastRun.id)} style={{ paddingLeft: 0 }}>
                    View all {changes.length} changes
                  </Button>
                )}
              </>
            )}
          </CardSection>
        </>
      )}
    </Card>
  );
}
